import { useState } from 'react';
import { t } from '@/i18n';
import { downloadBlob } from '@chikara/utils';
import { useToast } from '@chikara/ui';
import { useFilters } from '@chikara/hooks';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PageContent } from '@/shared/components/PageContent';
import { DashboardHeader } from '@/features/dashboard/components/DashboardHeader';
import { DashboardSummary } from '@/features/dashboard/components/DashboardSummary/DashboardSummary';
import { DashboardMovements } from '@/features/dashboard/components/DashboardMovements/DashboardMovements';
import { useDashboardSummary } from '@/features/dashboard/hooks/useDashboard/useDashboardSummary';
import { useDashboardMovements } from '@/features/dashboard/hooks/useDashboard/useDashboardMovements';
import { accountService } from '@/features/card/services/accountService/accountService';
import { isBasic } from '@/shared/config/permissions';
import type { DashboardFilters } from '@/features/dashboard/types/dashboard.types';
import { buildGenericFilters, buildMovementFilterCatalogs, getEffectiveRange } from '@/features/dashboard/utils/dashboardFilters.utils';
import { buildMovementsExportDirective, MOVEMENTS_PDF_FILENAME, MOVEMENTS_XLSX_FILENAME } from '@/features/dashboard/utils/dashboardExport.utils';
import type { ExportFormat } from '@/shared/export/types';
import { buildExportIdentity } from '@/shared/export/identity';



function resolveDashboardUserFirstName(
  fromSummary: string | undefined,
  memberName: string | undefined,
  userFirstName: string | undefined,
  fullName: string | undefined
): string {
  const a = fromSummary?.trim();
  if (a) return a;
  const b = memberName?.trim();
  if (b) return b;
  const c = userFirstName?.trim();
  if (c) return c;
  const d = fullName?.trim();
  if (d) {
    const first = d.split(/\s+/)[0];
    if (first) return first;
  }
  return '';
}

export default function DashboardPage() {
  const toast = useToast();
  const { userData, user, member, payload: authPayload } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const filters = useFilters<DashboardFilters>({});

  const customerUuid = authPayload?.customer_uuid || '';
  const userUuid = authPayload?.user_uuid;

  const { summary, userSpending, userDetails, customerDetails } = useDashboardSummary(userUuid || '');
  const { data: movements, total, catalogs } = useDashboardMovements(
    customerUuid,
    currentPage,
    userData.role,
    userUuid,
    filters.applied
  );

  const handleFilterChange = (patch: Partial<DashboardFilters>) => {
    filters.setDraftAll({ ...filters.draft, ...patch });
  };

  const handleApplyFilters = () => {
    filters.apply();
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    filters.clear();
    setCurrentPage(1);
  };

  const headerUserName = resolveDashboardUserFirstName(
    userDetails?.name,
    member?.name,
    user?.firstName,
    userData.fullName
  );

  const buildMovementExportRequest = () => {
    const effectiveRange = getEffectiveRange(filters.applied);
    const genericFilters = buildGenericFilters({
      ...filters.applied,
      from: effectiveRange.from,
      to: effectiveRange.to,
    });

    return {
      ...(isBasic(userData.role) && userUuid
        ? { user: { uuid: userUuid } }
        : { customer: { uuid: customerUuid } }),
      filter: {
        page_number: 1,
        results_per_page: total,
        genericFilters,
      },
    };
  };

  const buildMovementDirective = (format: ExportFormat) => {
    const user = isBasic(userData.role) && userUuid
      ? { name: headerUserName, email: userData.email ?? '' }
      : null;

    return buildMovementsExportDirective(
      {
        filters: filters.applied,
        ...buildExportIdentity({
          companyName: customerDetails?.legal_name,
          keyId: customerDetails?.key_id,
          customerUuid,
        }),
        user,
        userSpending: user ? userSpending : null,
      },
      format
    );
  };

  const handleExportXlsx = async () => {
    try {
      const { blob, filename } = await accountService.exportMovements(
        buildMovementExportRequest(),
        buildMovementDirective('xlsx')
      );
      downloadBlob(blob, filename ?? MOVEMENTS_XLSX_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  };

  const handleExportPdf = async () => {
    try {
      const { blob, filename } = await accountService.exportMovements(
        buildMovementExportRequest(),
        buildMovementDirective('pdf')
      );
      downloadBlob(blob, filename ?? MOVEMENTS_PDF_FILENAME);
    } catch {
      toast.error(t('export.errors.download'));
    }
  };

  return (
    <PageContent
      header={
        <DashboardHeader
          userName={headerUserName}
          companyName={customerDetails?.legal_name || ''}
          companyId={customerDetails?.key_id || ''}
        />
      }
    >
      <div>
          <DashboardSummary 
            summary={summary} 
            userSpending={userSpending} 
          />
        
        <DashboardMovements 
          data={movements} 
          total={total} 
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          draftFilters={filters.draft}
          onFilterChange={handleFilterChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          onExportExcel={handleExportXlsx}
          onExportPdf={handleExportPdf}
          catalogs={buildMovementFilterCatalogs(catalogs)}
        />
      </div>
    </PageContent>
  );
}

export { DashboardPage };
